import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { Loading } from "@/components/Loading";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { onlineOrderingNeedsSecondApp } from "@/features/applications/components/v2/pricing";
import { useBackTo } from "@/hooks/useBackTo";
import { useAuth } from "@/features/auth/auth";
import { toast } from "sonner";
import { ApiError } from "@/lib/apiClient";
import { url } from "@/lib/basePath";
import { toastApiError } from "@/lib/toast";
import { AgentAnswer, CompareDialog, MergeWithDialog } from "@/features/applications/components/DuplicatesPanel";
import { applicationsV2, UNDERWRITING, type DuplicatePair, type ApplicationV2Detail, type SaveApplicationV2Request, type V2Bag, type V2Value } from "@/features/applications/api/applications.api";
import { UserSelect } from "@/features/users/components/UserSelect";
import { Notice } from "@/components/Notice";
import { Dash } from "@/components/Dash";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronDown } from "lucide-react";
import { StatusPill, APPLICATION_STATUS_TONES, APPLICATION_STATUS_LABELS } from "@/components/StatusPill";
import {
  STEPS, VISIBLE_STEPS, ValidationContext, ReadOnlyContext, isLockedStatus, isPricingLocked, StatusTracker, PendedPill, AgentTip, isEmptyVal, validateField, overMaxLength, optionsFor, baseRateLinesFor, zipAutoFill,
  PRICING_FIELDS, PRICING_DEPENDENTS, AWAITING_AGENT_PRICING, PricingConfigurator, PricingSummary, ActivityPanel, UnderwritingPanel,
  rateExceptionOf, rateExceptionCovers, type RateExceptionActions,
  SectionCard, TotalBadge, BackLink, ReqBadge, CardedFields,
  ConfigStep, BusinessStep, FeesStep, DocumentsStep, ReviewStep, RepeatEditor,
  type FieldDef, type RepeatKey,
} from "@/features/applications/components/v2";

import { formatDateTime, toApiDateTime } from "@/utils/dates";
import { EquipmentChecklist } from "@/features/equipment/components/EquipmentChecklist";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";

const SELECT_NONE = "__none__";
const PRICING_BY_KEY = new Map(PRICING_FIELDS.map((d) => [d.key, d]));

function withoutHiddenPricing(bag: V2Bag, enabled: boolean): V2Bag {
  if (!enabled) return bag;
  let out = bag;
  for (const d of PRICING_FIELDS) {
    if (d.showWhen && !d.showWhen(out) && !isEmptyVal(out[d.key] ?? null)) out = { ...out, [d.key]: null };
  }
  return out;
}
const NO_ASSOCIATE_OPTIONS = [{ value: SELECT_NONE, label: "— None —" }];

type BoardMode = "manual" | "auto";

const IN_REVIEW = new Set(["Submitted", "New", "Pending"]);
const IN_UNDERWRITING = new Set(["ESignSent", "ESigned", "ActionNeeded", "Underwriting"]);
const AT_PROCESSOR = new Set([...IN_UNDERWRITING, "Approved", "Boarded", "Live"]);
const MARK_BOARDED_FROM = new Set([AWAITING_AGENT_PRICING, ...IN_REVIEW, ...IN_UNDERWRITING, "Approved"]);
const TSYS_MID = /^\d{6,20}$/;
const menuOpenFor = (status: string, hasMerchant: boolean, agent: boolean) =>
  agent
    ? status === "Draft" || status === AWAITING_AGENT_PRICING
    : status === "Draft" || status === AWAITING_AGENT_PRICING || IN_REVIEW.has(status) || IN_UNDERWRITING.has(status)
      || status === "Approved" || status === "Boarded" || (status === "Live" && hasMerchant);

const stepDateKeys = (defs: FieldDef[] | undefined): string[] =>
  (defs ?? []).filter((d) => d.type === "date").map((d) => d.key);

const FIELD_DATE_KEYS = new Set(STEPS.flatMap((s) => stepDateKeys(s.fields)));
const FEE_DATE_KEYS = new Set(STEPS.flatMap((s) => stepDateKeys(s.feeFields)));
const REPEAT_DATE_KEYS: Record<RepeatKey, Set<string>> = {
  principals: new Set(),
  bankAccounts: new Set(),
  posSetup: new Set(),
  tradeReferences: new Set(),
};
for (const s of STEPS) {
  if (s.repeat) {
    for (const k of stepDateKeys(s.repeat.rowFields)) REPEAT_DATE_KEYS[s.repeat.collection].add(k);
  }
}

const stepHasRequired = (s: (typeof STEPS)[number]): boolean => {
  if (s.fields?.some((d) => d.required)) return true;
  if (s.feeFields && s.feeFields.length > 0) return true;
  if (s.repeat) {
    if (s.repeat.minRows && s.repeat.minRows > 0) return true;
    if (s.repeat.rowFields.some((d) => d.required)) return true;
  }
  return false;
};
const REQUIRED_STEPS = new Set(
  VISIBLE_STEPS.filter((s) => !s.hideEsign && stepHasRequired(s)).map((s) => s.n),
);
const firstVisibleStep = VISIBLE_STEPS[0].n;
const clampToVisible = (n: number): number => {
  if (VISIBLE_STEPS.some((s) => s.n === n)) return n;
  const below = VISIBLE_STEPS.filter((s) => s.n <= n);
  return below.length ? below[below.length - 1].n : firstVisibleStep;
};

function normalizeDates(bag: V2Bag, keys: Set<string>): V2Bag {
  if (keys.size === 0) return bag;
  const out: V2Bag = { ...bag };
  for (const k of keys) {
    if (k in out) out[k] = toApiDateTime(out[k]);
  }
  return out;
}

export default function ApplicationV2WizardPage() {
  const { user } = useAuth();
  const viewerIsAgent = (d: { viewerIsBackOffice: boolean | null }) =>
    d.viewerIsBackOffice == null ? user?.role === "USER" : !d.viewerIsBackOffice;
  const canSeeActivity = user?.role === "ADMIN";
  const agentSignedLock = (d: { status: string; viewerIsBackOffice: boolean | null }) =>
    viewerIsAgent(d) && isPricingLocked(d.status);
  const { id } = useParams<{ id: string }>();
  const latestId = useRef(id);
  const tabsRef = useRef<HTMLDivElement>(null);
  latestId.current = id;
  const navigate = useNavigate();
  const backTo = useBackTo("/applications/v2");
  const confirm = useConfirm();

  const [detail, setDetail] = useState<ApplicationV2Detail | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicatePair[]>([]);
  const [reassigning, setReassigning] = useState(false);
  const [comparing, setComparing] = useState<DuplicatePair | null>(null);
  const [mergingWith, setMergingWith] = useState(false);
  const [unmerging, setUnmerging] = useState(false);
  const [mergedIntoVisible, setMergedIntoVisible] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [fields, setFields] = useState<V2Bag>({});
  const [feeSchedule, setFeeSchedule] = useState<V2Bag>({});
  const [principals, setPrincipals] = useState<V2Bag[]>([]);
  const [bankAccounts, setBankAccounts] = useState<V2Bag[]>([]);
  const [posSetup, setPosSetup] = useState<V2Bag[]>([]);
  const [tradeReferences, setTradeReferences] = useState<V2Bag[]>([]);
  const [associateUserId, setAssociateUserId] = useState<string | null>(null);

  const [step, setStep] = useState(1);
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<"application" | "pricing" | "activity" | "underwriting">(
    searchParams.get("tab") === "pricing" ? "pricing" : searchParams.get("tab") === "underwriting" ? "underwriting" : "application",
  );
  const [saving, setSaving] = useState(false);
  const [editingPricing, setEditingPricing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [crossErrors, setCrossErrors] = useState<{ step: number; message: string }[]>([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [reasonDialog, setReasonDialog] = useState<"decline" | "cancel" | null>(null);
  const [reasonText, setReasonText] = useState("");
  const [boardOpen, setBoardOpen] = useState(false);
  const [boardMode, setBoardMode] = useState<BoardMode>("manual");
  const [boardMid, setBoardMid] = useState("");
  const [boardMidError, setBoardMidError] = useState<string | null>(null);
  const [tsysOpen, setTsysOpen] = useState(false);
  const [tsysMid, setTsysMid] = useState("");
  const [tsysMidError, setTsysMidError] = useState<string | null>(null);

  const markFilled = useCallback((key: string) => {
    setMissing((prev) => {
      if (!prev.has(key)) return prev;
      const n = new Set(prev);
      n.delete(key);
      return n;
    });
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const n = new Map(prev);
      n.delete(key);
      return n;
    });
  }, []);

  const markMissing = useCallback((key: string) => {
    setMissing((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const n = new Map(prev);
      n.delete(key);
      return n;
    });
  }, []);

  const markError = useCallback((key: string, message: string) => {
    setErrors((prev) => {
      if (prev.get(key) === message) return prev;
      const n = new Map(prev);
      n.set(key, message);
      return n;
    });
    setMissing((prev) => {
      if (!prev.has(key)) return prev;
      const n = new Set(prev);
      n.delete(key);
      return n;
    });
  }, []);

  const sync = useCallback((d: ApplicationV2Detail) => {
    setDetail({ ...d, documents: d.documents ?? [] });
    setFields({ ...d.fields });
    setFeeSchedule(d.feeSchedule ? { ...d.feeSchedule } : {});
    setPrincipals((d.principals ?? []).map((r) => ({ ...r })));
    setBankAccounts((d.bankAccounts ?? []).map((r) => ({ ...r })));
    setPosSetup((d.posSetup ?? []).map((r) => ({ ...r })));
    setTradeReferences((d.tradeReferences ?? []).map((r) => ({ ...r })));
    setAssociateUserId(d.associate?.id ?? null);
  }, []);

  const refresh = useCallback(async () => {
    if (!id) return;
    sync(await applicationsV2.get(id));
  }, [id, sync]);

  const refreshDocuments = useCallback(async () => {
    if (!id) return;
    const d = await applicationsV2.get(id);
    setDetail((prev) => (prev ? { ...prev, documents: d.documents ?? [] } : { ...d, documents: d.documents ?? [] }));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setEditingPricing(false);
    applicationsV2.get(id)
      .then((d) => {
        if (cancelled) return;
        sync(d);
        setStep(clampToVisible(d.wizardStep));
        if (d.status === AWAITING_AGENT_PRICING) setView("pricing");
        else if (d.status === "ActionNeeded") setView("underwriting");
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else if (e instanceof ApiError && e.status === 404)
          setLoadError("Application not found, or you don't have access to it.");
        else setLoadError(e instanceof Error ? e.message : "Failed to load the application.");
      });
    return () => { cancelled = true; };
  }, [id, sync]);

  const detailStatus = detail?.status;
  const detailIsAgent = detail ? viewerIsAgent(detail) : true;
  useEffect(() => {
    if (!id || !detailStatus || detailStatus === "Merged") { setDuplicates([]); return; }
    let cancelled = false;
    (detailIsAgent ? applicationsV2.myDuplicates(id) : applicationsV2.duplicates(id))
      .then((p) => { if (!cancelled) setDuplicates(p); })
      .catch(() => { if (!cancelled) setDuplicates([]); });
    return () => { cancelled = true; };
  }, [id, detailIsAgent, detailStatus]);

  const mergedIntoId = detailStatus === "Merged" && typeof fields.mergedIntoApplicationId === "string" ? fields.mergedIntoApplicationId : null;
  useEffect(() => {
    if (!mergedIntoId || !detailIsAgent) { setMergedIntoVisible(null); return; }
    let cancelled = false;
    applicationsV2.get(mergedIntoId)
      .then(() => { if (!cancelled) setMergedIntoVisible(true); })
      .catch(() => { if (!cancelled) setMergedIntoVisible(false); });
    return () => { cancelled = true; };
  }, [mergedIntoId, detailIsAgent]);

  useEffect(() => {
    if (detail?.status !== "Draft") return;
    if (step === 1) {
      setFields((p) => ({
        ...p,
        includePatriotAct: true,
        includeCardAssocDisclosure: true,
        includePetroFleetAddendum: false,
        includeFdgLeasing: false,
        fleetIndicator: false,
        fuelIndicator: false,
        includeEsignature: p.includeEsignature ?? true,
      }));
    }
    if (step === 3) {
      setPrincipals((p) => p.length === 0
        ? [{ isBeneficialOwner: true, isIndividualWithControl: true, isSigningApp: true, isPersonalGuarantor: true, countryOfResidence: "United States" }]
        : p);
    }
    if (step === 5) {
      setBankAccounts((b) => b.length === 0
        ? [{ purpose: "Deposit", accountType: "Checking" }]
        : b);
    }
  }, [step, detail?.status]);

  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const changeField = useCallback((key: string, value: V2Value) => {
    const isPricing = PRICING_BY_KEY.has(key);
    const next = withoutHiddenPricing({ ...fieldsRef.current, [key]: value }, isPricing);
    fieldsRef.current = next;
    setFields((p) => withoutHiddenPricing({ ...p, [key]: value }, isPricing));
    for (const dep of PRICING_DEPENDENTS[key] ?? []) {
      const def = PRICING_BY_KEY.get(dep)!;
      const val = next[dep] ?? null;
      if (def.showWhen && !def.showWhen(next)) { markFilled(dep); continue; }
      if (isEmptyVal(val)) {
        if (!(def.required || def.requiredWhen?.(next))) markFilled(dep);
        continue;
      }
      const msg = validateField(def, val, next);
      if (msg) markError(dep, msg); else markFilled(dep);
    }
    zipAutoFill(key, value, (patch) => {
      setFields((p) => ({ ...p, ...patch }));
      for (const k of Object.keys(patch)) markFilled(k);
    });
  }, [markFilled, markError]);

  const collections = useMemo(
    () => ({ principals, bankAccounts, posSetup, tradeReferences }),
    [principals, bankAccounts, posSetup, tradeReferences],
  );
  const collectionSetters: Record<RepeatKey, Dispatch<SetStateAction<V2Bag[]>>> = {
    principals: setPrincipals,
    bankAccounts: setBankAccounts,
    posSetup: setPosSetup,
    tradeReferences: setTradeReferences,
  };

  function buildRequest(): SaveApplicationV2Request {
    const withPosition = (rows: V2Bag[]) => rows.map((r, i) => ({ ...r, position: i }));
    return {
      wizardStep: step,
      associateUserId,
      fields: normalizeDates(fields, FIELD_DATE_KEYS),
      feeSchedule: normalizeDates(feeSchedule, FEE_DATE_KEYS),
      principals: withPosition(principals).map((r) => normalizeDates(r, REPEAT_DATE_KEYS.principals)),
      bankAccounts: withPosition(bankAccounts).map((r) => normalizeDates(r, REPEAT_DATE_KEYS.bankAccounts)),
      posSetup: withPosition(posSetup).map((r) => normalizeDates(r, REPEAT_DATE_KEYS.posSetup)),
      tradeReferences: withPosition(tradeReferences).map((r) => normalizeDates(r, REPEAT_DATE_KEYS.tradeReferences)),
    };
  }

  async function save(): Promise<boolean> {
    if (!id) return false;
    if (detail && (isLockedStatus(detail.status) || agentSignedLock(detail))) {
      toast.error(`This application is ${APPLICATION_STATUS_LABELS[detail.status] ?? detail.status} and can no longer be edited.`);
      return false;
    }
    setSaving(true);
    try {
      await applicationsV2.save(id, buildRequest());
      await refresh();
      setSavedAt(new Date().toISOString());
      setEditingPricing(false);
      return true;
    } catch (e) {
      toastApiError(e, "Save failed.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  type Issue = { step: number; key: string; kind: "required" | "rule" | "choice"; message: string };

  const isUnlistedChoice = (d: FieldDef, val: V2Value): boolean => {
    if (d.type !== "select" || d.radio || !d.options || d.options.length === 0) return false;
    if (typeof val !== "string" || val === "") return false;
    return !d.options.some((o) => (typeof o === "string" ? o : o.value) === val);
  };

  function scanFields(out: Issue[], defs: FieldDef[] | undefined, bag: V2Bag, n: number, prefix = "") {
    for (const d of defs ?? []) {
      if (d.showWhen && !d.showWhen(bag)) continue;
      const val = bag[d.key] ?? null;
      const isReq = d.required || (d.requiredWhen ? d.requiredWhen(bag) : false);
      if (isReq && isEmptyVal(val)) {
        out.push({ step: n, key: prefix + d.key, kind: "required", message: "Required." });
        continue;
      }
      if (isUnlistedChoice(d, val)) {
        out.push({ step: n, key: prefix + d.key, kind: "choice", message: "Pick a value from the list." });
        continue;
      }
      const msg = validateField(d, val, bag);
      if (msg) { out.push({ step: n, key: prefix + d.key, kind: "rule", message: msg }); continue; }
      if (overMaxLength(d.maxLength, val)) out.push({ step: n, key: prefix + d.key, kind: "rule", message: tooLong(d.label, d.maxLength!) });
    }
  }

  function collectPricingIssues(): Issue[] {
    const out: Issue[] = [];
    scanFields(out, PRICING_FIELDS, fields, 0);
    return out;
  }

  function collectIssues(): Issue[] {
    const out: Issue[] = [];
    const scan = (defs: FieldDef[] | undefined, bag: V2Bag, n: number, prefix = "") =>
      scanFields(out, defs, bag, n, prefix);
    for (const s of STEPS) {
      if (s.hidden) continue;
      if (!s.hideEsign) scan(s.fields, fields, s.n);
      scan(s.feeFields, feeSchedule, s.n);
      if (s.feeFields) {

        const str = (k: string) => (typeof feeSchedule[k] === "string" ? (feeSchedule[k] as string) : "");
        const procF = str("processingType"), planF = str("pricingPlan"), optionF = str("pricingPlanOption");
        if (isEmptyVal(feeSchedule.processingType ?? null)) out.push({ step: s.n, key: "processingType", kind: "required", message: "Required." });
        if (isEmptyVal(feeSchedule.pricingPlan ?? null)) out.push({ step: s.n, key: "pricingPlan", kind: "required", message: "Required." });
        if (optionsFor(procF, planF).length > 0 && isEmptyVal(feeSchedule.pricingPlanOption ?? null))
          out.push({ step: s.n, key: "pricingPlanOption", kind: "required", message: "Required." });
        for (const line of baseRateLinesFor(procF, planF, optionF)) {
          if (line.required && isEmptyVal(feeSchedule[line.pctKey] ?? null)) out.push({ step: s.n, key: line.pctKey, kind: "required", message: "Required." });
        }
      }
      if (s.repeat) {
        const rows = collections[s.repeat.collection];
        if (s.repeat.minRows && rows.length < s.repeat.minRows) {
          out.push({ step: s.n, key: `${s.repeat.collection}.__min__`, kind: "required", message: "Required." });
        }
        rows.forEach((row, i) => {
          scan(s.repeat!.rowFields, row, s.n, `${s.repeat!.collection}.${i}.`);
        });
      }
    }

    const SSN_EXEMPT = ["Government", "Financial Institution", "Corporation - Public"];
    if (!SSN_EXEMPT.includes(fields.ownershipType as string)) {
      principals.forEach((row, i) => {
        if (row.skipCreditPull === true) return;
        if (row.isUsCitizen === false && !isEmptyVal(row.passportNumber ?? null) && !isEmptyVal(row.countryOfResidence ?? null)) return;
        if (isEmptyVal(row.ssn ?? null)) {
          out.push({ step: 3, key: `principals.${i}.ssn`, kind: "required", message: "Required." });
        }
      });
    }

    return out;
  }

  function collectCrossErrors(): { step: number; message: string }[] {
    const out: { step: number; message: string }[] = [];
    if (methodTotal !== 100) {
      out.push({ step: 4, message: `Card processing method percentages must total 100% (currently ${methodTotal}%).` });
    }
    if (principals.length > 0 && ownershipTotal < 51) {
      out.push({ step: 3, message: `Combined ownership across owners must be at least 51% (currently ${ownershipTotal.toFixed(2)}%).` });
    }
    if (fields.hasAcceptedCardsBefore === true && !(detail?.documents ?? []).some((d) => d.documentType === "ProcessingStatement")) {
      out.push({ step: 10, message: "Upload at least one previous processing statement (most recent 3 months)." });
    }
    if (principals.length > 0) {
      const SIGN_EXEMPT = ["Government", "Corporation - Public", "Non-Profit"];
      if (SIGN_EXEMPT.includes(fields.ownershipType as string)) {
        if (!principals.some((p) => p.isIndividualWithControl === true)) {
          out.push({ step: 3, message: "This entity type requires at least one individual with control." });
        }
      } else if (!principals.some((p) => p.isBeneficialOwner === true && p.isSigningApp === true)) {
        out.push({ step: 3, message: "At least one beneficial owner must be marked as signing the application." });
      }
    }
    return out;
  }

  async function submit() {
    if (!id) return;
    if (detail && isLockedStatus(detail.status)) {
      toast.error(`This application is ${detail.status} and can no longer be edited.`);
      return;
    }
    if (detail?.pricingRequired !== false && flagPricingGaps("Configure pricing before submitting this application.")) return;
    const issues = collectIssues();
    const cross = collectCrossErrors();
    if (issues.length > 0 || cross.length > 0) {
      setSubmitAttempted(true);
      setMissing(new Set(issues.filter((i) => i.kind === "required").map((i) => i.key)));
      setErrors(new Map(issues.filter((i) => i.kind !== "required").map((i) => [i.key, i.message])));
      setCrossErrors(cross);
      const firstStep = Math.min(...[...issues.map((i) => i.step), ...cross.map((c) => c.step)]);
      setStep(firstStep);
      if (issues.length > 0) {
        toast.error("Please fix the highlighted fields before submitting.");
      }
      requestAnimationFrame(() => {
        document.querySelector('[data-missing="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    setSubmitAttempted(false);
    setMissing(new Set());
    setErrors(new Map());
    setCrossErrors([]);
    setSubmitting(true);
    try {
      await applicationsV2.save(id, buildRequest());
      await applicationsV2.submit(id);
      await refresh();
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      toastApiError(e, "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  }

  function flagPricingGaps(message: string): boolean {
    const gaps = collectPricingIssues();
    if (gaps.length === 0) return false;
    setMissing(new Set(gaps.filter((i) => i.kind === "required").map((i) => i.key)));
    setErrors(new Map(gaps.filter((i) => i.kind !== "required").map((i) => [i.key, i.message])));
    setView("pricing");
    toast.error(message);
    requestAnimationFrame(() => {
      document.querySelector('[data-missing="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return true;
  }

  async function sendToBackOffice() {
    if (!id) return;
    if (flagPricingGaps("Configure pricing before submitting to back office.")) return;
    setSubmitAttempted(false);
    setMissing(new Set());
    setErrors(new Map());
    setSubmitting(true);
    try {
      await applicationsV2.save(id, buildRequest());
      await applicationsV2.submit(id);
      await refresh();
      setEditingPricing(false);
      toast.success("Submitted. Your application is being reviewed.");
    } catch (e) {
      toastApiError(e, "Couldn't submit to back office.");
    } finally {
      setSubmitting(false);
    }
  }

  async function approve() {
    if (!id) return;
    const unlisted = collectIssues().filter((i) => i.kind === "choice");
    if (unlisted.length > 0) {
      setErrors(new Map(unlisted.map((i) => [i.key, i.message])));
      setStep(Math.min(...unlisted.map((i) => i.step)));
      toast.error("Some fields hold a value that isn't in the list. Pick a valid value before approving.");
      requestAnimationFrame(() => {
        document.querySelector('[data-missing="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    if (!(await confirm({ title: "Mark approved?", description: "Underwriting approved this application. You can board it as a merchant next.", confirmLabel: "Mark approved" }))) return;
    setSubmitting(true);
    try {
      await applicationsV2.save(id, buildRequest());
      await applicationsV2.approve(id);
      navigate(backTo);
    } catch (e) {
      toastApiError(e, "Couldn't mark it approved.");
    } finally {
      setSubmitting(false);
    }
  }

  const rateApprovalWaiting = () => rateExceptionOf(fields)?.status === "Pending" && rateExceptionCovers(fields);

  async function cancelPricingEdit() {
    if (!id) return;
    try {
      const d = await applicationsV2.get(id);
      if (latestId.current !== id) return;
      const saved = d.fields ?? {};
      const keys = PRICING_FIELDS.map((f) => f.key);
      setFields((p) => {
        const next = { ...p };
        for (const key of keys) {
          if (key in saved) next[key] = saved[key];
          else delete next[key];
        }
        return next;
      });
      for (const key of keys) markFilled(key);
      setEditingPricing(false);
    } catch (e) {
      toastApiError(e, "Couldn't reload pricing.");
    }
  }

  async function requestRateException(reason: string): Promise<boolean> {
    if (!id) return false;
    setSubmitting(true);
    try {
      await applicationsV2.save(id, buildRequest());
      await applicationsV2.requestRateException(id, reason);
      await refresh();
      toast.success("Exception requested. Every admin was notified.");
      return true;
    } catch (e) {
      toastApiError(e, "Couldn't request the exception.");
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function reassignOwner(userId: string) {
    if (!id) return;
    if (!(await confirm({
      title: "Change the owner?",
      description: `${detail?.owner?.name ?? "The current agent"} will lose access to this application. The new owner gets a notice and all its updates from now on.`,
      confirmLabel: "Change owner",
    }))) return;
    setReassigning(true);
    try {
      await applicationsV2.reassignOwner(id, userId);
      await refresh();
      toast.success("Owner changed. The new agent was notified.");
    } catch (e) {
      toastApiError(e, "Couldn't change the owner.");
    } finally {
      setReassigning(false);
    }
  }

  async function answerDuplicate(otherId: string, same: boolean) {
    if (!id) return;
    try {
      await applicationsV2.answerDuplicate(id, otherId, same);
      setDuplicates(await applicationsV2.myDuplicates(id));
      toast.success(same ? "Thanks. Onboarding has been told and will combine them." : "Got it. We won't ask again.");
    } catch (e) {
      toastApiError(e, "Couldn't save your answer.");
    }
  }

  async function unmerge() {
    if (!id) return;
    if (!(await confirm({
      title: "Un-merge this application?",
      description: "It comes back with its old status, and its own files, TSYS emails, underwriting messages, equipment orders and pricing move back to it. Anything added to the kept application since the merge stays there.",
      confirmLabel: "Un-merge",
    }))) return;
    setUnmerging(true);
    try {
      await applicationsV2.unmerge(id);
      await refresh();
      toast.success("Un-merged. It's back in the Applications list.");
    } catch (e) {
      toastApiError(e, "Couldn't un-merge this application.");
    } finally {
      setUnmerging(false);
    }
  }

  async function addMissingPricing() {
    if (!id || flagPricingGaps("Complete the pricing before saving.")) return;
    setSaving(true);
    try {
      const pricing = Object.fromEntries(PRICING_FIELDS.filter((d) => d.key in fields).map((d) => [d.key, fields[d.key]]));
      await applicationsV2.addMissingPricing(id, pricing);
      await refresh();
      toast.success("Pricing saved for our records.");
    } catch (e) {
      toastApiError(e, "Couldn't save the pricing.");
    } finally {
      setSaving(false);
    }
  }

  async function decideRateException(approve: boolean, note: string | null): Promise<boolean> {
    if (!id) return false;
    setSubmitting(true);
    try {
      await applicationsV2.decideRateException(id, approve, note);
      await refresh();
      toast.success(approve ? "Exception approved. The agent was notified." : "Exception declined. The agent was notified.");
      return true;
    } catch (e) {
      toastApiError(e, "Couldn't save the decision.");
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function sendToProcessor() {
    if (!id) return;
    if (rateApprovalWaiting()) {
      toast.error("Waiting on rate approval. An admin has to approve the rate exception first.");
      return;
    }
    if (detail?.pricingRequired !== false && flagPricingGaps("Configure pricing before submitting this application.")) return;
    const name = ((fields.dbaName as string) || "").trim();
    let sameName = 0;
    if (name) {
      try {
        const others = await applicationsV2.list({ pageSize: 10, status: UNDERWRITING, filter: { dbaName: name } });
        sameName = others.items.filter((a) => a.id !== id && !a.processorMid).length;
      } catch {
        sameName = 0;
      }
    }
    const needsOnline = fields.onlineOrdering === true
      && onlineOrderingNeedsSecondApp(fields.pricingProgram, fields.pricingRatePct)
      && !fields.onlineApplicationId
      && !fields.onlineOfApplicationId;
    const sameNameWarning = sameName > 0
      ? `Another application named exactly "${name}" is already waiting on the processor. Add a number (for example "${name} 2") before submitting, so processor emails match the right one. `
      : "";
    const inStorePricing = [
      typeof fields.pricingProgram === "string" ? fields.pricingProgram : null,
      fields.pricingRatePct != null && fields.pricingRatePct !== "" ? `${fields.pricingRatePct}%` : null,
    ].filter(Boolean).join(" ")
      + (fields.pricingPerTxnFee != null && fields.pricingPerTxnFee !== "" ? ` + $${Number(fields.pricingPerTxnFee).toFixed(2)}` : "");
    const appName = name || "This business";
    const twoApps = (
      <span className="block space-y-3 text-left">
        <span className="block">Online ordering needs its own account, so {appName} gets two applications.</span>
        <span className="block rounded-md border border-slate-200 px-3 py-2">
          <span className="block break-words text-sm font-medium text-slate-900">{appName}</span>
          <span className="block text-xs text-slate-500">In-store{inStorePricing ? ` · ${inStorePricing}` : ""}</span>
        </span>
        <span className="block rounded-md border border-slate-200 px-3 py-2">
          <span className="mb-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">New</span>
          <span className="block break-words text-sm font-medium text-slate-900">{appName} Online</span>
          <span className="block text-xs text-slate-500">Online ordering · Flat Rate 3.3% + $0.30</span>
        </span>
        {sameNameWarning && <span className="block text-xs text-amber-700">{sameNameWarning.trim()}</span>}
        <span className="block text-xs">The merchant signs both. Pricing locks after this.</span>
      </span>
    );
    if (!(await confirm({
      title: needsOnline ? "Sent 2 applications for e-sign?" : "Sent for e-sign?",
      description: needsOnline
        ? twoApps
        : sameName > 0
          ? `${sameNameWarning}Mark it sent anyway?`
          : "Confirm you entered this application in TSYS and the merchant got the e-sign email. The agent gets reminders until the merchant signs. Pricing locks after this.",
      confirmLabel: needsOnline ? "Yes, both sent" : "Yes, sent for e-sign",
    }))) return;
    setSubmitting(true);
    try {
      const request = buildRequest();
      await applicationsV2.save(id, request);
      await applicationsV2.sendToProcessor(id, needsOnline ? request : undefined);
      await refresh();
      toast.success(needsOnline
        ? "Submitted for processing. 2 applications are now E-sign sent."
        : "Submitted for processing. Status: E-sign sent.");
    } catch (e) {
      toastApiError(e, "Couldn't submit the application.");
    } finally {
      setSubmitting(false);
    }
  }

  async function moveStatus(status: string, label: string) {
    if (!id) return;
    if (!(await confirm({ title: `${label}?`, description: `Change the status to ${APPLICATION_STATUS_LABELS[status] ?? status}.`, confirmLabel: label }))) return;
    setSubmitting(true);
    try {
      await applicationsV2.moveStatus(id, status, detail?.status ?? "");
      await refresh();
      toast.success(`Status changed to ${APPLICATION_STATUS_LABELS[status] ?? status}.`);
    } catch (e) {
      toastApiError(e, "Couldn't change the status.");
      await refresh();
    } finally {
      setSubmitting(false);
    }
  }

  function openBoardDialog() {
    setBoardMode("manual");
    setBoardMid("");
    setBoardMidError(null);
    setBoardOpen(true);
  }

  async function board() {
    if (!id) return;
    let mid: string | null = null;
    if (boardMode === "manual") {
      mid = boardMid.trim();
      if (!mid) {
        setBoardMidError("Required.");
        return;
      }
      if (mid.length > FIELD_MAX.merchant.mid) {
        setBoardMidError(tooLong("Merchant ID", FIELD_MAX.merchant.mid));
        return;
      }
    }
    setBoardMidError(null);
    setSubmitting(true);
    try {
      const { id: merchantId } = await applicationsV2.board(id, mid);
      setBoardOpen(false);
      if (merchantId) {
        window.location.assign(url(`merchants/${merchantId}`));
        return;
      }
      await refresh();
    } catch (e) {
      if (mid !== null && e instanceof ApiError && (e.status === 400 || e.status === 409)) {
        setBoardMidError(e.message || "Couldn't board with this Merchant ID.");
      } else {
        setBoardOpen(false);
        toastApiError(e, "Board failed.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function openUnderwritingChat() {
    setView("underwriting");
    requestAnimationFrame(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  function openTsysDialog() {
    setTsysMid(detail?.processorMid ?? "");
    setTsysMidError(null);
    setTsysOpen(true);
  }

  async function markBoardedAtTsys() {
    if (!id) return;
    const mid = tsysMid.trim();
    if (!TSYS_MID.test(mid)) {
      setTsysMidError("Enter the TSYS MID (6 to 20 digits).");
      return;
    }
    setTsysMidError(null);
    setSubmitting(true);
    try {
      const { id: merchantId } = await applicationsV2.markBoarded(id, mid);
      setTsysOpen(false);
      toast.success("Marked boarded at TSYS.");
      if (merchantId) {
        window.location.assign(url(`merchants/${merchantId}`));
        return;
      }
      await refresh();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 400 || e.status === 409)) {
        setTsysMidError(e.message || "Couldn't mark it boarded.");
      } else {
        setTsysOpen(false);
        toastApiError(e, "Couldn't mark it boarded.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function openReasonDialog(kind: "decline" | "cancel") {
    setReasonText("");
    setReasonDialog(kind);
  }

  async function confirmReason() {
    if (!id || !reasonDialog) return;
    const kind = reasonDialog;
    const reason = reasonText.trim() || null;
    setReasonDialog(null);
    setSubmitting(true);
    try {
      if (kind === "decline") await applicationsV2.decline(id, reason);
      else await applicationsV2.cancel(id, reason);
      navigate(backTo);
    } catch (e) {
      const verb = kind === "decline" ? "Decline" : "Cancel";
      toastApiError(e, `${verb} failed.`);
    } finally {
      setSubmitting(false);
    }
  }

  if (forbidden) {
    return (
      <>
        <BackLink />
        <Notice tone="warn">
          You don't have permission to view boarding applications.
        </Notice>
      </>
    );
  }
  if (loadError) {
    return (
      <>
        <BackLink />
        <Notice tone="error">{loadError}</Notice>
      </>
    );
  }
  if (!detail) {
    return (
      <>
        <BackLink />
        <Loading>Loading application…</Loading>
      </>
    );
  }

  const current = STEPS[step - 1];
  const visibleIndex = VISIBLE_STEPS.findIndex((s) => s.n === step);
  const goPrev = () => { if (visibleIndex > 0) setStep(VISIBLE_STEPS[visibleIndex - 1].n); };
  const goNext = () => { if (visibleIndex >= 0 && visibleIndex < VISIBLE_STEPS.length - 1) setStep(VISIBLE_STEPS[visibleIndex + 1].n); };
  const title = (fields.dbaName as string) || (fields.legalName as string) || "Untitled application";
  const savedLabel = formatDateTime(savedAt ?? detail.updatedAt);
  const isAgentView = viewerIsAgent(detail);
  const mergedFrom = Array.isArray(fields.mergedFromApplicationIds)
    ? fields.mergedFromApplicationIds.filter((m): m is string => typeof m === "string")
    : [];
  const signedLock = agentSignedLock(detail);
  const readOnly = isLockedStatus(detail.status) || signedLock;
  const pricingLocked = isPricingLocked(detail.status);
  const awaitingPricing = detail.status === AWAITING_AGENT_PRICING;
  const exceptionActions: RateExceptionActions = {
    canDecide: !isAgentView,
    busy: saving || submitting,
    onRequest: requestRateException,
    onDecide: decideRateException,
  };
  const pricingConfigured = collectPricingIssues().length === 0;
  const showUnderwriting = detail.underwritingRequested || detail.status === "ActionNeeded" || (!isAgentView && AT_PROCESSOR.has(detail.status));
  const pricingSent = detail.status !== "Draft" && detail.status !== AWAITING_AGENT_PRICING;
  const savedPricingIssues: Issue[] = [];
  scanFields(savedPricingIssues, PRICING_FIELDS, detail.fields ?? {}, 0);
  const pricingMissing = !isAgentView && pricingLocked && savedPricingIssues.length > 0 && detail.pricingRequired;
  const showPricingSummary = !pricingMissing && pricingSent && pricingConfigured && !rateApprovalWaiting() && (pricingLocked || readOnly || !editingPricing);
  const pricingNeeded = !readOnly && detail.status !== "Draft" && !pricingConfigured && detail.pricingRequired;
  const merchantName = [fields.dbaName, fields.legalName]
    .find((v): v is string => typeof v === "string" && v.trim() !== "")?.trim() ?? "The merchant";

  const num = (v: V2Value) => (typeof v === "number" ? v : 0);
  const methodTotal =
    num(fields.pctCardSwiped) + num(fields.pctManuallyKeyedImprint) +
    num(fields.pctManuallyKeyedNoImprint) + num(fields.pctEcommerce) + num(fields.pctB2B);
  const ownershipTotal = principals.reduce((s, p) => s + num(p.ownershipPercentage), 0);

  const blockedSteps = new Set<number>([
    ...collectIssues().map((i) => i.step),
    ...collectCrossErrors().map((c) => c.step),
  ]);
  const isStepComplete = (n: number) => {
    if (detail.status !== "Draft" && STEPS[n - 1]?.review) return true;
    return REQUIRED_STEPS.has(n) && !blockedSteps.has(n);
  };

  function copyAddr(from: "legal" | "dba", to: "legal" | "dba") {
    const parts = ["Name", "Address", "Suite", "City", "State", "Zip", "Phone", "Fax"];
    setFields((p) => {
      const next = { ...p };
      for (const part of parts) next[`${to}${part}`] = p[`${from}${part}`] ?? null;
      return next;
    });
  }

  return (
    <>
      <BackLink />

      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{title}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-slate-500">
            {savedLabel && <span>Saved {savedLabel}</span>}
            {detail.status === "ActionNeeded" ? (
              <PendedPill onClick={openUnderwritingChat} />
            ) : (
              <StatusPill
                value={detail.status}
                map={APPLICATION_STATUS_TONES}
                labels={APPLICATION_STATUS_LABELS}
              />
            )}
            {detail.processorMid && <span className="font-mono text-xs text-slate-600">MID {detail.processorMid}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {menuOpenFor(detail.status, !!detail.merchantId, isAgentView) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" disabled={saving || submitting}>
                  Actions <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isAgentView && detail.status === AWAITING_AGENT_PRICING && (
                  <DropdownMenuItem onClick={sendToBackOffice}>Submit to back office</DropdownMenuItem>
                )}
                {!isAgentView && IN_REVIEW.has(detail.status) && (
                  <DropdownMenuItem disabled={submitting || rateApprovalWaiting()} onClick={sendToProcessor}>
                    {rateApprovalWaiting() ? "Sent for e-sign (waiting on rate approval)" : "Sent for e-sign"}
                  </DropdownMenuItem>
                )}
                {!isAgentView && detail.status === "ESignSent" && (
                  <DropdownMenuItem onClick={() => moveStatus("ESigned", "Mark e-signed")}>Mark e-signed</DropdownMenuItem>
                )}
                {!isAgentView && (detail.status === "ESignSent" || detail.status === "ESigned" || detail.status === "Underwriting") && (
                  <DropdownMenuItem onClick={() => moveStatus("ActionNeeded", "Mark pended")}>Mark pended</DropdownMenuItem>
                )}
                {!isAgentView && detail.status === "ActionNeeded" && (
                  <DropdownMenuItem onClick={() => moveStatus("ESigned", "Items sent to underwriting")}>Items sent to underwriting</DropdownMenuItem>
                )}
                {!isAgentView && IN_UNDERWRITING.has(detail.status) && (
                  <DropdownMenuItem onClick={approve}>Mark approved</DropdownMenuItem>
                )}
                {!isAgentView && detail.status === "Approved" && (
                  <DropdownMenuItem onClick={openBoardDialog}>Board as merchant</DropdownMenuItem>
                )}
                {!isAgentView && !detail.merchantId && MARK_BOARDED_FROM.has(detail.status) && (
                  <DropdownMenuItem onClick={openTsysDialog}>Mark boarded at TSYS…</DropdownMenuItem>
                )}
                {!isAgentView && detail.status === "Boarded" && (
                  <DropdownMenuItem onClick={() => moveStatus("Live", "Mark live")}>Mark live</DropdownMenuItem>
                )}
                {(detail.status === "Boarded" || detail.status === "Live") && detail.merchantId && (
                  <DropdownMenuItem asChild>
                    <a href={`/merchants/${detail.merchantId}`}>Open merchant →</a>
                  </DropdownMenuItem>
                )}
                {!isAgentView && (
                  <DropdownMenuItem onClick={() => setMergingWith(true)}>Merge with…</DropdownMenuItem>
                )}
                {!isAgentView && (IN_REVIEW.has(detail.status) || IN_UNDERWRITING.has(detail.status) || detail.status === "Approved") && (
                  <DropdownMenuItem
                    onClick={() => openReasonDialog("decline")}
                    className="text-destructive focus:text-destructive"
                  >
                    Decline
                  </DropdownMenuItem>
                )}
                {(detail.status === "Draft" || detail.status === AWAITING_AGENT_PRICING || IN_REVIEW.has(detail.status)
                  || IN_UNDERWRITING.has(detail.status) || detail.status === "Approved") && (
                  <DropdownMenuItem onClick={() => openReasonDialog("cancel")}>Cancel</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!readOnly && (
            <Button type="button" onClick={save} disabled={saving || submitting}>
              {saving ? "Saving…" : "Save"}
            </Button>
          )}
        </div>
      </header>

      <StatusTracker
        status={detail.status}
        statusSince={detail.statusSince}
        agentName={detail.owner?.name}
        onOpenPended={openUnderwritingChat}
        aside={
          <>
            <EquipmentChecklist applicationId={detail.id} status={detail.status} dbaName={typeof fields.dbaName === "string" ? fields.dbaName : null} />
            {duplicates.map((p) => {
              const other = p.a.id === detail.id ? p.b : p.a;
              if (isAgentView && p.agentAnswer === "same")
                return <span key={other.id} className="text-slate-600">✓ You said it's the same business</span>;
              return (
                <span key={other.id} className="inline-flex flex-wrap items-center gap-1.5 text-amber-800" title={`Another ${other.name ?? "application"} (${APPLICATION_STATUS_LABELS[other.status] ?? other.status})`}>
                  ⚠ Possible duplicate
                  {!isAgentView && p.agentAnswer && <>· <AgentAnswer answer={p.agentAnswer} /></>}
                  <button type="button" onClick={() => setComparing(p)} className="font-medium text-blue-700 hover:underline">Compare</button>
                </span>
              );
            })}
            {!isAgentView && mergedFrom.length > 0 && (
              <span className="text-slate-600">
                Merged from {mergedFrom.length === 1 ? "1 other" : `${mergedFrom.length} others`} ·{" "}
                {mergedFrom.map((m, i) => (
                  <span key={m}>
                    {i > 0 && ", "}
                    <Link to={`/applications/v2/${m}`} className="font-medium text-blue-700 hover:underline">{mergedFrom.length === 1 ? "View" : `View ${i + 1}`}</Link>
                  </span>
                ))}
              </span>
            )}
          </>
        }
      />

      {mergingWith && (
        <MergeWithDialog
          app={{ id: detail.id, name: typeof fields.dbaName === "string" && fields.dbaName ? fields.dbaName : (fields.legalName as string) ?? null }}
          agentName={detail.owner?.name ?? null}
          onClose={() => setMergingWith(false)}
          onMerged={() => {
            setMergingWith(false);
            void refresh();
            if (id) applicationsV2.duplicates(id).then(setDuplicates).catch(() => setDuplicates([]));
          }}
        />
      )}

      {comparing && (
        <CompareDialog
          a={comparing.a.id}
          b={comparing.b.id}
          openKept
          onAnswer={isAgentView ? (same) => { const other = comparing.a.id === detail.id ? comparing.b : comparing.a; setComparing(null); void answerDuplicate(other.id, same); } : undefined}
          onClose={() => setComparing(null)}
          onMerged={() => {
            setComparing(null);
            void refresh();
            if (id) applicationsV2.duplicates(id).then(setDuplicates).catch(() => setDuplicates([]));
          }}
        />
      )}

      <div ref={tabsRef} />
      <Tabs value={view} onValueChange={(v) => setView(v as "application" | "pricing" | "activity" | "underwriting")}>
        <TabsList variant="underline" className="mb-6" aria-label="Application sections">
          <TabsTrigger variant="underline" value="application">Application</TabsTrigger>
          <TabsTrigger variant="underline" value="pricing">
            Configure pricing
            {!readOnly && !pricingConfigured && detail.pricingRequired && (
              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Needed</span>
            )}
          </TabsTrigger>
          {showUnderwriting && (
            <TabsTrigger variant="underline" value="underwriting">
              Underwriting
              {detail.status === "ActionNeeded" && <span className="ml-2 h-2 w-2 rounded-full bg-red-500" aria-label="Needs a reply" />}
            </TabsTrigger>
          )}
          {canSeeActivity && <TabsTrigger variant="underline" value="activity">Activity</TabsTrigger>}
        </TabsList>
      </Tabs>

      {view === "application" && (
        <div className="flex flex-wrap gap-1.5 mb-6">
          {VISIBLE_STEPS.map((s, i) => (
            <button
              key={s.n}
              type="button"
              onClick={() => setStep(s.n)}
              className={`text-xs px-2.5 py-1.5 rounded border transition-colors ${
                s.n === step
                  ? "bg-slate-800 text-white border-slate-800"
                  : submitAttempted && blockedSteps.has(s.n)
                    ? "bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                    : isStepComplete(s.n)
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {i + 1}. {s.label}
            </button>
          ))}
        </div>
      )}

      <ReadOnlyContext.Provider value={readOnly}>
      <ValidationContext.Provider value={{ missing, errors, markFilled, markMissing, markError, prefix: "" }}>
      {view === "underwriting" && showUnderwriting ? (
        <UnderwritingPanel appId={detail.id} agentIds={[detail.owner?.id, detail.associate?.id]} isAgent={isAgentView} business={(fields.dbaName as string) || (fields.legalName as string) || undefined} note={detail.status === "ActionNeeded" ? detail.underwritingNote : null} />
      ) : view === "activity" && canSeeActivity ? (
        <ActivityPanel id={detail.id} reloadKey={`${detail.status}|${detail.updatedAt}`} />
      ) : view === "pricing" ? (
        <div className="space-y-5">
          {showPricingSummary ? (
            <PricingSummary
              bag={fields}
              locked={readOnly || pricingLocked}
              onEdit={readOnly || pricingLocked ? undefined : () => setEditingPricing(true)}
            />
          ) : (
            <>
              {pricingMissing ? (
                <Notice tone="warn" className="px-3 py-2">
                  TSYS already has this merchant's signed pricing. Enter it here for our records.
                </Notice>
              ) : pricingLocked && (
                <Notice tone="info" className="px-3 py-2">
                  🔒 Pricing is locked because this application was submitted for processing.
                </Notice>
              )}
              {editingPricing && pricingSent && (
                <Notice tone="info" className="px-3 py-2">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span>Editing pricing. Click Save to keep your changes.</span>
                    <Button type="button" variant="ghost" size="sm" disabled={saving || submitting} onClick={cancelPricingEdit}>
                      Cancel
                    </Button>
                  </div>
                </Notice>
              )}
              <ReadOnlyContext.Provider value={!pricingMissing && (readOnly || pricingLocked)}>
                <PricingConfigurator bag={fields} onChange={changeField} onSubmit={awaitingPricing && isAgentView ? sendToBackOffice : undefined} onSave={pricingMissing ? () => void addMissingPricing() : pricingSent || (awaitingPricing && !isAgentView) ? () => { if (!flagPricingGaps("Complete the pricing before saving.")) void save(); } : undefined} submitting={submitting} saving={saving} exceptionActions={exceptionActions} />
              </ReadOnlyContext.Provider>
            </>
          )}
        </div>
      ) : (
      <div className="space-y-5">
        {current.banner && <Notice tone="info" className="px-3 py-2">{current.banner}</Notice>}
        {crossErrors.filter((c) => c.step === current.n).map((c, i) => (
          <Notice key={i} tone="error">{c.message}</Notice>
        ))}
        {current.n === 3 && principals.length > 0 && (
          <TotalBadge
            label="Combined ownership (principals owning 25%+ must be listed)"
            value={Number(ownershipTotal.toFixed(2))}
            ok={ownershipTotal >= 51}
            hint="must total at least 51%"
          />
        )}

        {current.n === 1 && (
          <SectionCard title="Configuration">
            <div className="mb-6 pb-6 border-b border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-xs font-medium text-slate-600">Owner</Label>
                {!isAgentView && detail.status !== "Merged" ? (
                  <UserSelect
                    value={detail.owner?.id ?? ""}
                    disabled={reassigning}
                    onValueChange={(v) => { if (v && v !== detail.owner?.id) void reassignOwner(v); }}
                  />
                ) : (
                  <div className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                    {detail.owner?.name || <Dash />}
                  </div>
                )}
              </div>
              <div>
                <Label className="mb-1 block text-xs font-medium text-slate-600">Associate user</Label>
                <UserSelect
                  value={associateUserId ?? SELECT_NONE}
                  disabled={readOnly}
                  onValueChange={(v) => setAssociateUserId(v === SELECT_NONE ? null : v)}
                  pinnedOptions={NO_ASSOCIATE_OPTIONS}
                />
              </div>
            </div>
            {current.fields && (
              <ConfigStep fields={current.fields} bag={fields} onChange={changeField} />
            )}
          </SectionCard>
        )}

        {current.n === 2 && current.fields && (
          <BusinessStep
            fields={current.fields}
            bag={fields}
            onChange={changeField}
            onCopy={copyAddr}
          />
        )}

        {current.fields && current.n !== 1 && current.n !== 2 && !current.review && !current.documents && (
          <>
            {current.n === 4 && (
              <TotalBadge label="Processing method split" value={methodTotal} ok={methodTotal === 100} hint="must total 100%" />
            )}
            <CardedFields fields={current.fields} bag={fields} onChange={changeField} />
          </>
        )}

        {current.feeFields && (
          <FeesStep feeFields={current.feeFields} bag={feeSchedule} onChange={(k, v) => setFeeSchedule((p) => ({ ...p, [k]: v }))} />
        )}

        {current.repeat && (
          <RepeatEditor
            title={current.repeat.title}
            noun={current.repeat.noun}
            collection={current.repeat.collection}
            rowFields={current.repeat.rowFields}
            rows={collections[current.repeat.collection]}
            onChange={collectionSetters[current.repeat.collection]}
            onSave={save}
          />
        )}

        {current.documents && (
          <DocumentsStep
            appId={detail.id}
            documents={detail.documents}
            onChanged={refreshDocuments}
            fields={current.fields ?? []}
            bag={fields}
            onChange={changeField}
          />
        )}

        {current.review && (
          <ReviewStep
            fields={current.fields ?? []}
            bag={fields}
            onChange={changeField}
            status={detail.status}
            submitting={submitting}
            saving={saving}
            onSubmit={submit}
            onConfigurePricing={() => setView("pricing")}
            showEsign={!current.hideEsign}
          />
        )}

        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrev}
            disabled={visibleIndex === 0}
          >
            ← Previous
          </Button>
          <span className="text-xs text-slate-400">Step {visibleIndex + 1} of {VISIBLE_STEPS.length}</span>
          <Button
            type="button"
            variant="outline"
            onClick={goNext}
            disabled={visibleIndex === VISIBLE_STEPS.length - 1}
          >
            Next →
          </Button>
        </div>
      </div>
      )}
      </ValidationContext.Provider>
      </ReadOnlyContext.Provider>

      <div className="mt-8 space-y-3 empty:hidden">
      {detail.status === "Merged" && isAgentView && mergedIntoVisible === false && (
        <div>
          <Notice tone="info">
            This merchant also applied through another agent, and onboarding kept the other application. Questions? Contact onboarding.
          </Notice>
        </div>
      )}
      {detail.status === "Merged" && typeof fields.mergedIntoApplicationId === "string" && (!isAgentView || mergedIntoVisible === true) && (
        <div>
          <Notice tone="info">
            This application was merged.{" "}
            <Link to={`/applications/v2/${fields.mergedIntoApplicationId}`} className="font-medium underline">Open the application it was merged into</Link>
            {canSeeActivity && fields.mergeUndo != null && (
              <Button type="button" size="sm" variant="outline" className="ml-3" disabled={unmerging} onClick={unmerge}>
                {unmerging ? "Un-merging…" : "Un-merge"}
              </Button>
            )}
          </Notice>
        </div>
      )}
      {readOnly && detail.status !== "Merged" && (
        <div>
          <Notice tone="warn">
            {signedLock
              ? "This application was sent for signature, so it's view-only. Owner, bank or business changes need a change form."
              : `This application is ${APPLICATION_STATUS_LABELS[detail.status] ?? detail.status} and is view-only. Its data can no longer be edited, added to, removed or saved.`}
          </Notice>
        </div>
      )}
      {(awaitingPricing || pricingNeeded || pricingMissing) && (
        <div
          className={`rounded-lg border-2 p-4 ${
            pricingConfigured && !pricingMissing ? "border-emerald-400 bg-emerald-50" : "border-amber-400 bg-amber-50"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className={`text-base font-semibold ${pricingConfigured && !pricingMissing ? "text-emerald-900" : "text-amber-900"}`}>
                {pricingConfigured && !pricingMissing
                  ? "✅ Pricing configured! Ready for onboarding."
                  : pricingMissing
                    ? "⚠️ Pricing missing. Add it for our records."
                    : awaitingPricing
                      ? `🎉 ${merchantName} completed their onboarding form! Configure pricing to onboard`
                      : `Configure pricing for ${merchantName} so back office can approve it.`}
              </p>
            </div>
            <div className="flex gap-2">
              {(!pricingConfigured || pricingMissing) && view !== "pricing" && (
                <Button type="button" size="sm" onClick={() => setView("pricing")}>
                  {pricingMissing ? "Add pricing" : "Configure pricing"}
                </Button>
              )}
              {awaitingPricing && pricingConfigured && isAgentView && (
                <Button type="button" size="sm" onClick={sendToBackOffice} disabled={saving || submitting}>
                  {submitting ? "Submitting…" : "Submit"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {isAgentView && <AgentTip status={detail.status} />}
      {typeof fields.onlineApplicationId === "string" && fields.onlineApplicationId && (
        <Notice tone="info" className="px-3 py-2">
          Online ordering is on a separate application:{" "}
          <Link to={`/applications/v2/${fields.onlineApplicationId}`} className="font-medium text-blue-700 hover:underline">
            {(fields.dbaName as string) || "This business"} Online →
          </Link>
        </Notice>
      )}
      {typeof fields.onlineOfApplicationId === "string" && fields.onlineOfApplicationId && (
        <Notice tone="info" className="px-3 py-2">
          Created for online ordering (card-not-present) from the{" "}
          <Link to={`/applications/v2/${fields.onlineOfApplicationId}`} className="font-medium text-blue-700 hover:underline">
            in-store application →
          </Link>
        </Notice>
      )}
      </div>

      <Dialog open={boardOpen} onOpenChange={(o) => { if (!o && !submitting) setBoardOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Board as merchant</DialogTitle>
            <DialogDescription>Create a merchant from this approved application.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Merchant ID setup
            </div>
            <RadioGroup
              value={boardMode}
              disabled={submitting}
              onValueChange={(v) => { setBoardMode(v as BoardMode); setBoardMidError(null); }}
              className="gap-3"
            >
              <div className="space-y-2">
                <Label htmlFor="board-mode-manual" className="flex items-center gap-2 text-sm font-normal text-slate-700">
                  <RadioGroupItem id="board-mode-manual" value="manual" />
                  Enter Merchant ID
                </Label>
                {boardMode === "manual" && (
                  <div className="ml-6 space-y-1.5">
                    <Label htmlFor="board-mid" className="flex items-center gap-1 text-xs font-medium text-slate-600">
                      Merchant ID (MID) <ReqBadge />
                    </Label>
                    <Input
                      id="board-mid"
                      autoFocus
                      value={boardMid}
                      maxLength={FIELD_MAX.merchant.mid}
                      placeholder="Enter Merchant ID"
                      disabled={submitting}
                      aria-invalid={boardMidError !== null}
                      onChange={(e) => { setBoardMid(e.target.value); setBoardMidError(null); }}
                    />
                    <p className={`text-xs ${boardMidError ? "text-destructive" : "text-slate-500"}`}>
                      {boardMidError ?? `Max ${FIELD_MAX.merchant.mid} characters.`}
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="board-mode-auto" className="flex items-center gap-2 text-sm font-normal text-slate-700">
                  <RadioGroupItem id="board-mode-auto" value="auto" />
                  Use a temporary Merchant ID
                </Label>
                {boardMode === "auto" && (
                  <p className="ml-6 text-xs text-slate-500">
                    A new merchant will be created with a temporary MID in the format PENDING-NNNN.
                    You can update it after the processor returns the real MID.
                  </p>
                )}
              </div>
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setBoardOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" onClick={board} disabled={submitting}>
              {submitting ? "Boarding…" : "Board merchant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={tsysOpen} onOpenChange={(o) => { if (!o && !submitting) setTsysOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark boarded at TSYS</DialogTitle>
            <DialogDescription>
              Use this when TSYS already boarded this merchant. The application moves to Boarded and the merchant is created. Portal pricing isn't required.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="tsys-mid" className="flex items-center gap-1 text-xs font-medium text-slate-600">
              TSYS MID <ReqBadge />
            </Label>
            <Input
              id="tsys-mid"
              autoFocus
              inputMode="numeric"
              value={tsysMid}
              maxLength={20}
              placeholder="e.g. 39300983918946"
              disabled={submitting}
              aria-invalid={tsysMidError !== null}
              aria-describedby="tsys-mid-help"
              onChange={(e) => { setTsysMid(e.target.value); setTsysMidError(null); }}
            />
            <p id="tsys-mid-help" className={`text-xs ${tsysMidError ? "text-destructive" : "text-slate-500"}`}>
              {tsysMidError ?? "Unmatched TSYS emails for this MID are cleared from Needs a person."}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setTsysOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" onClick={markBoardedAtTsys} disabled={submitting}>
              {submitting ? "Marking…" : "Mark boarded"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reasonDialog !== null} onOpenChange={(o) => !o && setReasonDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reasonDialog === "decline" ? "Decline application" : "Cancel application"}
            </DialogTitle>
            <DialogDescription>
              Optional reason — visible to admins in the audit log.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            autoFocus
            rows={4}
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder="Reason (optional)"
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setReasonDialog(null)}>
              Keep application
            </Button>
            <Button
              type="button"
              variant={reasonDialog === "decline" ? "destructive" : "default"}
              onClick={confirmReason}
            >
              {reasonDialog === "decline" ? "Decline" : "Cancel application"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
